import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useKlapScript } from "@/hooks/useKlapScript";
import {
  payPlanWithKlap,
  getListPagosPlanPorEmpresaId,
  claimFreeTrial,
} from "@/features/suscripcion/suscripcionThunks";
import { resetKlapOrderId } from "@/features/suscripcion/suscripcionSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion as Motion, AnimatePresence } from "motion/react";
import Modal from "@/components/Modals/Modal";

// Precios de planes desde variables de entorno (en pesos chilenos) [PLAN_PRICES]
const PLAN_PRICES = {
  "1_mes": parseInt(import.meta.env.VITE_PLAN_1_MES_PRICE || "25000"),
  "3_meses": parseInt(import.meta.env.VITE_PLAN_3_MESES_PRICE || "60000"),
  "12_meses": parseInt(import.meta.env.VITE_PLAN_12_MESES_PRICE || "180000"),
};

// Precios normales (sin descuento)
const NORMAL_PRICES = {
  "1_mes": 50000,
  "3_meses": 150000,
  "12_meses": 600000,
};

const DISCOUNT_PCT = {
  "1_mes": Math.round((1 - PLAN_PRICES["1_mes"] / NORMAL_PRICES["1_mes"]) * 100),
  "3_meses": Math.round((1 - PLAN_PRICES["3_meses"] / NORMAL_PRICES["3_meses"]) * 100),
  "12_meses": Math.round((1 - PLAN_PRICES["12_meses"] / NORMAL_PRICES["12_meses"]) * 100),
};

const SuscripcionTab = ({ empresaInfo, empresaId }) => {
  const dispatch = useDispatch();
  const { pagos, klapOrderId, isLoadingSuscripcion, errorSuscripcion } =
    useSelector((state) => state.suscripcion);

  const { isReady: isKlapReady } = useKlapScript();

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [claimingFreeTrial, setClaimingFreeTrial] = useState(false);
  const [showBeneficios, setShowBeneficios] = useState(false);

  // Estados para modales
  const [noPlanSelectedModal, setNoPlanSelectedModal] = useState(false);
  const [noEmpresaIdModal, setNoEmpresaIdModal] = useState(false);
  const [errorPagoModal, setErrorPagoModal] = useState(false);
  const [errorFreeTrialModal, setErrorFreeTrialModal] = useState(false);
  const [successFreeTrialModal, setSuccessFreeTrialModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    // Cargar pagos realizados cuando se monta el componente
    if (empresaInfo?.uid) {
      dispatch(getListPagosPlanPorEmpresaId({ idEmpresa: empresaInfo.uid }));
    }
  }, [dispatch, empresaInfo?.uid]);

  // Auto-cerrar modal de éxito y recargar página después de 5 segundos
  useEffect(() => {
    if (successFreeTrialModal) {
      const timer = setTimeout(() => {
        setSuccessFreeTrialModal(false);
        window.location.reload();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successFreeTrialModal]);

  // Cuando se recibe el klapOrderId, abrir el checkout de KLAP
  useEffect(() => {
    // Abrir checkout de KLAP solo si klapOrderId está definido, se está procesando el pago y el script está listo
    if (klapOrderId && processingPayment && isKlapReady) {
      console.log(
        "[Suscripción] Abriendo checkout de KLAP con orderId:",
        klapOrderId,
      );

      // Llamar a la función global de KLAP
      if (window.payKlapOrder) {
        window.payKlapOrder(klapOrderId);
      } else {
        console.error(
          "[Suscripción] La función payKlapOrder no está disponible",
        );
      }

      setProcessingPayment(false);
      setSelectedPlan(null);

      // Limpiar el klapOrderId del estado de Redux después de abrir el checkout
      // para que la próxima vez se genere uno nuevo
      setTimeout(() => {
        dispatch(resetKlapOrderId());
      }, 1000); // Delay de 1 segundo para asegurar que el modal de KLAP se abrió
    }
  }, [klapOrderId, processingPayment, isKlapReady, dispatch]);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
  };

  const handlePayPlan = async () => {
    console.log("[Suscripción] handlePayPlan llamado", selectedPlan);
    if (!selectedPlan) {
      setNoPlanSelectedModal(true);
      return;
    }

    if (!empresaInfo?.uid) {
      setNoEmpresaIdModal(true);
      return;
    }

    try {
      setProcessingPayment(true);
      await dispatch(
        payPlanWithKlap({
          idEmpresa: empresaInfo.uid,
          plan: selectedPlan,
        }),
      ).unwrap();
    } catch (error) {
      console.error("[Suscripción] Error al iniciar pago:", error);
      setErrorMessage(error.message || "Error al procesar el pago");
      setErrorPagoModal(true);
      setProcessingPayment(false);
      setSelectedPlan(null);
    }
  };

  const handleClaimFreeTrial = async () => {
    console.log("[Suscripción] handleClaimFreeTrial llamado");

    if (!empresaInfo?.uid) {
      setNoEmpresaIdModal(true);
      return;
    }

    try {
      setClaimingFreeTrial(true);
      const result = await dispatch(
        claimFreeTrial({
          idEmpresa: empresaInfo.uid,
        }),
      ).unwrap();

      setSuccessMessage(
        result.message || "¡Plan de prueba activado exitosamente!",
      );
      setSuccessFreeTrialModal(true);

      // Recargar pagos para mostrar el registro del plan de prueba
      dispatch(getListPagosPlanPorEmpresaId({ idEmpresa: empresaInfo.uid }));
    } catch (error) {
      console.error("[Suscripción] Error al reclamar plan de prueba:", error);
      setErrorMessage(error.message || "Error al reclamar el plan de prueba");
      setErrorFreeTrialModal(true);
    } finally {
      setClaimingFreeTrial(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("es-CL", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getPlanLabel = (planType) => {
    switch (planType) {
      case "1_mes":
        return "1 Mes";
      case "3_meses":
        return "3 Meses";
      case "12_meses":
        return "12 Meses";
      case "14_dias_prueba":
        return "Prueba Gratuita (14 Días)";
      default:
        return planType;
    }
  };

  const planOptions = [
    {
      id: "1_mes",
      name: "Plan Mensual",
      duration: "1 Mes",
      price: PLAN_PRICES["1_mes"],
      normalPrice: NORMAL_PRICES["1_mes"],
      discountPct: DISCOUNT_PCT["1_mes"],
      savings: null,
      icon: "fa-calendar-days",
      popular: false,
    },
    {
      id: "3_meses",
      name: "Plan Trimestral",
      duration: "3 Meses",
      price: PLAN_PRICES["3_meses"],
      normalPrice: NORMAL_PRICES["3_meses"],
      discountPct: DISCOUNT_PCT["3_meses"],
      savings: `Ahorras ${Math.round((1 - PLAN_PRICES["3_meses"] / (PLAN_PRICES["1_mes"] * 3)) * 100)}% vs mensual`,
      icon: "fa-dice-three",
      popular: true,
    },
    {
      id: "12_meses",
      name: "Plan Anual",
      duration: "12 Meses",
      price: PLAN_PRICES["12_meses"],
      normalPrice: NORMAL_PRICES["12_meses"],
      discountPct: DISCOUNT_PCT["12_meses"],
      savings: `Ahorras ${Math.round((1 - PLAN_PRICES["12_meses"] / (PLAN_PRICES["1_mes"] * 12)) * 100)}% vs mensual`,
      icon: "fa-explosion",
      popular: false,
    },
  ];

  return (
    <div className="space-y-8">
      {/* Estado Actual de la Suscripción */}
      <div className="bg-aw-bluedark rounded-xl border border-gray-600 p-6">
        <h2 className="text-2xl font-bold text-white mb-4">
          <FontAwesomeIcon
            icon="fa-solid fa-crown"
            className="mr-2 text-aw-green"
          />
          Estado de tu Suscripción
        </h2>
        {empresaInfo?.planActivo ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Plan Actual:</span>
              <span className="text-white font-semibold text-lg text-right">
                {getPlanLabel(empresaInfo.planTipo)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Estado:</span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-500/20 text-green-400">
                <FontAwesomeIcon
                  icon="fa-solid fa-check-circle"
                  className="mr-2"
                />
                Activo
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Vigencia hasta:</span>
              <span className="text-white font-semibold text-right">
                {formatDate(empresaInfo.planVigencia)}
              </span>
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <FontAwesomeIcon
              icon="fa-solid fa-exclamation-circle"
              className="text-yellow-500 text-4xl mb-3"
            />
            <p className="text-gray-300 text-lg">No tienes un plan activo</p>
            <p className="text-gray-400 text-sm mt-2">
              Selecciona un plan abajo para activar las funciones premium
            </p>
          </div>
        )}
      </div>

      {/* Selector de Planes */}
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-2xl font-bold text-white">
            <FontAwesomeIcon
              icon="fa-solid fa-ticket"
              className="mr-2 text-aw-green"
            />
            Activa tu Plan
          </h2>
          <button
            onClick={() => setShowBeneficios(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-aw-green/15 border border-aw-green/50 text-aw-green text-sm font-semibold hover:bg-aw-green/25 transition-colors shrink-0"
          >
            <FontAwesomeIcon icon="fa-solid fa-star" />
            Ver Beneficios
          </button>
        </div>
        {/*  Prueba gratuita de 14 días */}
        {!empresaInfo?.plan && (
          <div className="flex justify-between items-center text-center text-white mb-4 bg-aw-bluedark p-4 rounded-lg border border-aw-green">
            <p>Comienza tu prueba gratuita de 14 días</p>
            <button
              onClick={handleClaimFreeTrial}
              disabled={claimingFreeTrial}
              className={`bg-aw-green text-aw-blueblack px-4 py-2 rounded-lg font-semibold transition-transform hover:scale-95 duration-200 ${
                claimingFreeTrial
                  ? "opacity-50 cursor-not-allowed"
                  : "animate-bounce"
              }`}
            >
              {claimingFreeTrial ? "Procesando..." : "Reclamar Prueba Gratuita"}
            </button>
          </div>
        )}

        {/* Banner de lanzamiento */}
        <div className="relative overflow-hidden bg-gradient-to-r from-aw-green/20 via-aw-green/10 to-aw-green/20 border border-aw-green/60 rounded-xl p-5">
          <div className="absolute top-0 right-0 w-32 h-32 bg-aw-green/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-aw-green text-aw-blueblack animate-pulse">
                <FontAwesomeIcon icon="fa-solid fa-bolt" />
                OFERTA DE LANZAMIENTO
              </span>
              <span className="text-gray-400">· Para las primeras 100 empresas</span>
            </div>
            <p className="text-white font-bold text-lg leading-tight mb-1">
              Hasta un <span className="text-aw-green">70% de descuento</span> en todos los planes
            </p>
            <p className="text-gray-300 text-sm">
              Somos nuevos y queremos que lo pruebes sin riesgos. Por tiempo limitado, los primeros 100 negocios que confíen en nosotros acceden a precios especiales de lanzamiento. <span className="text-aw-green font-medium">No pierdas tu lugar.</span>
            </p>
          </div>
        </div>

        {/* Opciones de Planes */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {planOptions.map((plan) => (
            <div
              key={plan.id}
              onClick={() => handleSelectPlan(plan.id)}
              className={`relative bg-aw-bluedark rounded-xl border-2 p-6 cursor-pointer transition-all hover:scale-95 ${
                selectedPlan === plan.id
                  ? "border-aw-green shadow-lg shadow-aw-green/50"
                  : "border-gray-600 hover:border-aw-green/50"
              } ${plan.popular ? "ring-2 ring-aw-green/30" : ""}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-aw-green text-aw-blueblack">
                    MÁS POPULAR
                  </span>
                </div>
              )}

              <div className="text-center space-y-3">
                <FontAwesomeIcon
                  icon={`fa-solid ${plan.icon}`}
                  className="text-4xl text-aw-green"
                />
                <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                <p className="text-gray-400 text-sm">{plan.duration}</p>
                {/* Precio original tachado */}
                <div className="flex items-center justify-center gap-2">
                  <span className="text-aw-green/50 text-sm line-through">
                    {formatCurrency(plan.normalPrice)}
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-500/20 text-red-400">
                    -{plan.discountPct}%
                  </span>
                </div>
                {/* Precio de lanzamiento */}
                <div className="text-3xl font-bold text-aw-green">
                  {formatCurrency(plan.price)}
                </div>
                <div className="text-gray-400 text-xs">precio de lanzamiento</div>
                {plan.savings && (
                  <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-400">
                    {plan.savings}
                  </div>
                )}
                {selectedPlan === plan.id && (
                  <div className="flex items-center justify-center text-aw-green text-sm font-semibold">
                    <FontAwesomeIcon
                      icon="fa-solid fa-check-circle"
                      className="mr-2"
                    />
                    Seleccionado
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Botón de Pago */}
        <div className="flex justify-center pt-4">
          <button
            onClick={handlePayPlan}
            disabled={
              !selectedPlan || processingPayment || isLoadingSuscripcion || !isKlapReady
            }
            className={`px-8 py-3 rounded-lg font-semibold text-lg transition-all ${
              !selectedPlan || processingPayment || isLoadingSuscripcion || !isKlapReady
                ? "bg-gray-600 text-gray-400 cursor-not-allowed"
                : "bg-aw-green text-aw-blueblack hover:bg-aw-green/90 hover:scale-95"
            }`}
          >
            {processingPayment || isLoadingSuscripcion ? (
              <>
                <FontAwesomeIcon
                  icon="fa-solid fa-spinner"
                  className="mr-2 animate-spin"
                />
                Procesando...
              </>
            ) : !isKlapReady ? (
              <>
                <FontAwesomeIcon
                  icon="fa-solid fa-spinner"
                  className="mr-2 animate-spin"
                />
                Cargando pasarela...
              </>
            ) : (
              <>
                <FontAwesomeIcon
                  icon="fa-solid fa-credit-card"
                  className="mr-2"
                />
                Pagar con KLAP
              </>
            )}
          </button>
        </div>

        {errorSuscripcion && (
          <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 text-center">
            <p className="text-red-400">{errorSuscripcion}</p>
          </div>
        )}
      </div>

      {/* Historial de Pagos */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-white">
          <FontAwesomeIcon
            icon="fa-solid fa-history"
            className="mr-2 text-aw-green"
          />
          Historial de Pagos
        </h2>
        {pagos && pagos.length > 0 ? (
          <div className="bg-aw-bluedark rounded-xl border border-gray-600 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-aw-bluedark-75">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Fecha
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {pagos.map((pago) => (
                    <tr
                      key={pago.pagoId}
                      className="hover:bg-aw-bluedark-75 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {formatDate(pago.fechaPago)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-medium">
                        {pago.planDescripcion}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-white font-semibold">
                        {formatCurrency(pago.monto)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            pago.estado === "completado"
                              ? "bg-green-500/20 text-green-400"
                              : "bg-yellow-500/20 text-yellow-400"
                          }`}
                        >
                          {pago.estado === "completado"
                            ? "Completado"
                            : pago.estado}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-aw-bluedark rounded-xl border border-gray-600 p-8 text-center">
            <FontAwesomeIcon
              icon="fa-solid fa-receipt"
              className="text-gray-600 text-5xl mb-4"
            />
            <p className="text-gray-400">No hay pagos registrados aún</p>
          </div>
        )}
      </div>

      {/* Modales */}
      {noPlanSelectedModal && (
        <Modal
          isOpen={noPlanSelectedModal}
          onClose={() => setNoPlanSelectedModal(false)}
          large="max-w-lg"
        >
          <div className="p-6 text-center bg-aw-bgbluedark rounded-lg">
            <FontAwesomeIcon
              icon="fa-solid fa-exclamation-triangle"
              className="text-yellow-500 mb-4"
              size="3x"
            />
            <h3 className="text-lg text-yellow-500 font-semibold">
              Selecciona un plan
            </h3>
            <p className="mt-2 text-gray-200">
              Por favor selecciona un plan antes de continuar con el pago.
            </p>
            <button
              onClick={() => setNoPlanSelectedModal(false)}
              className="mt-4 px-6 py-2 bg-aw-green text-aw-bluedark rounded-lg font-semibold hover:scale-95 transition-all"
            >
              Entendido
            </button>
          </div>
        </Modal>
      )}

      {noEmpresaIdModal && (
        <Modal
          isOpen={noEmpresaIdModal}
          onClose={() => setNoEmpresaIdModal(false)}
          large="max-w-lg"
        >
          <div className="p-6 text-center bg-aw-bgbluedark rounded-lg">
            <FontAwesomeIcon
              icon="fa-solid fa-circle-xmark"
              className="text-red-500 mb-4"
              size="3x"
            />
            <h3 className="text-lg text-red-500 font-semibold">
              Error de identificación
            </h3>
            <p className="mt-2 text-gray-200">
              No se pudo obtener el ID de la empresa. Por favor, recarga la
              página e intenta nuevamente.
            </p>
            <button
              onClick={() => setNoEmpresaIdModal(false)}
              className="mt-4 px-6 py-2 bg-aw-green text-aw-bluedark rounded-lg font-semibold hover:scale-95 transition-all"
            >
              Cerrar
            </button>
          </div>
        </Modal>
      )}

      {errorPagoModal && (
        <Modal
          isOpen={errorPagoModal}
          onClose={() => setErrorPagoModal(false)}
          large="max-w-lg"
        >
          <div className="p-6 text-center bg-aw-bgbluedark rounded-lg">
            <FontAwesomeIcon
              icon="fa-solid fa-circle-xmark"
              className="text-red-500 mb-4"
              size="3x"
            />
            <h3 className="text-lg text-red-500 font-semibold">
              Error al procesar el pago
            </h3>
            <p className="mt-2 text-gray-200">{errorMessage}</p>
            <button
              onClick={() => setErrorPagoModal(false)}
              className="mt-4 px-6 py-2 bg-aw-green text-aw-bluedark rounded-lg font-semibold hover:scale-95 transition-all"
            >
              Cerrar
            </button>
          </div>
        </Modal>
      )}

      {errorFreeTrialModal && (
        <Modal
          isOpen={errorFreeTrialModal}
          onClose={() => setErrorFreeTrialModal(false)}
          large="max-w-lg"
        >
          <div className="p-6 text-center bg-aw-bgbluedark rounded-lg">
            <FontAwesomeIcon
              icon="fa-solid fa-circle-xmark"
              className="text-red-500 mb-4"
              size="3x"
            />
            <h3 className="text-lg text-red-500 font-semibold">
              Error al reclamar plan de prueba
            </h3>
            <p className="mt-2 text-gray-200">{errorMessage}</p>
            <button
              onClick={() => setErrorFreeTrialModal(false)}
              className="mt-4 px-6 py-2 bg-aw-green text-aw-bluedark rounded-lg font-semibold hover:scale-95 transition-all"
            >
              Cerrar
            </button>
          </div>
        </Modal>
      )}

      {successFreeTrialModal && (
        <Modal
          isOpen={successFreeTrialModal}
          onClose={() => setSuccessFreeTrialModal(false)}
          large="max-w-lg"
        >
          <div className="p-6 text-center bg-aw-bgbluedark rounded-lg">
            <FontAwesomeIcon
              icon="fa-solid fa-circle-check"
              className="text-aw-green mb-4"
              size="3x"
            />
            <h3 className="text-lg text-aw-green font-semibold">
              ¡Plan de prueba activado!
            </h3>
            <p className="mt-2 text-gray-200">{successMessage}</p>
            <p className="mt-2 text-gray-200">
              Tu plan de prueba gratuito de 14 días ha sido activado
              exitosamente.
            </p>
            <p className="mt-4 text-gray-400 text-sm animate-pulse">
              Recargando la página en 5 segundos...
            </p>
          </div>
        </Modal>
      )}
      {/* Modal Beneficios */}
      {createPortal(
        <AnimatePresence>
          {showBeneficios && (
            <Motion.div
              key="modal-beneficios"
              initial={{ opacity: 0, top: "-100%" }}
              animate={{ opacity: 1, top: "0" }}
              exit={{ opacity: 0, top: "-100%" }}
              className="fixed inset-0 bg-aw-bluedark-30 flex items-center justify-center z-[99]"
              onClick={() => setShowBeneficios(false)}
              aria-modal="true"
              role="dialog"
            >
              <Motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.1, opacity: 0 }}
                transition={{ duration: 0.35, ease: "easeInOut" }}
                className="relative max-w-lg w-full mx-4 max-h-[85dvh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setShowBeneficios(false)}
                  className="absolute top-3 right-4 text-white hover:text-gray-400 font-semibold duration-300 transition-colors z-50 text-lg"
                  aria-label="Cerrar modal"
                >
                  ✕
                </button>
                <div className="bg-aw-bgbluedark rounded-xl inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green p-6 overflow-y-auto">
                  <h3 className="text-aw-green font-bold text-xl mb-1 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-solid fa-star" />
                    Todos los planes incluyen
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    Acceder a herramientas exclusivas para potenciar tus eventos y maximizar tu alcance.
                  </p>

                  <div className="space-y-4">
                    {/* Embajadores */}
                    <div className="bg-aw-bluedark rounded-lg border border-aw-green/30 p-4">
                      <p className="text-white font-semibold mb-2 flex items-center gap-2">
                        <FontAwesomeIcon icon="fa-solid fa-user-group" className="text-aw-green" />
                        Selección exclusiva de Embajadores
                      </p>
                      <ul className="space-y-1 text-gray-300 text-sm">
                        <li className="flex items-start gap-2">
                          <FontAwesomeIcon icon="fa-solid fa-check" className="text-aw-green mt-0.5 shrink-0" />
                          Invita a embajadores específicos a tus eventos
                        </li>
                        <li className="flex items-start gap-2">
                          <FontAwesomeIcon icon="fa-solid fa-check" className="text-aw-green mt-0.5 shrink-0" />
                          Control total sobre quién puede promocionar tu evento
                        </li>
                        <li className="flex items-start gap-2">
                          <FontAwesomeIcon icon="fa-solid fa-check" className="text-aw-green mt-0.5 shrink-0" />
                          Activa o desactiva embajadores según tu estrategia
                        </li>
                      </ul>
                    </div>

                    {/* Invitaciones */}
                    <div className="bg-aw-bluedark rounded-lg border border-aw-green/30 p-4">
                      <p className="text-white font-semibold mb-2 flex items-center gap-2">
                        <FontAwesomeIcon icon="fa-solid fa-envelope-open-text" className="text-aw-green" />
                        Invitaciones y control de admisión
                      </p>
                      <ul className="space-y-1 text-gray-300 text-sm">
                        <li className="flex items-start gap-2">
                          <FontAwesomeIcon icon="fa-solid fa-check" className="text-aw-green mt-0.5 shrink-0" />
                          Habilita postulaciones para controlar el derecho de admisión
                        </li>
                        <li className="flex items-start gap-2">
                          <FontAwesomeIcon icon="fa-solid fa-check" className="text-aw-green mt-0.5 shrink-0" />
                          Concede pases gratuitos, con descuento o de pago total
                        </li>
                        <li className="flex items-start gap-2">
                          <FontAwesomeIcon icon="fa-solid fa-check" className="text-aw-green mt-0.5 shrink-0" />
                          Define limite de cupo gratuito
                        </li>
                        <li className="flex items-start gap-2">
                          <FontAwesomeIcon icon="fa-solid fa-check" className="text-aw-green mt-0.5 shrink-0" />
                          Configura horario límite para cupo gratuito y descuento
                        </li>
                        <li className="flex items-start gap-2">
                          <FontAwesomeIcon icon="fa-solid fa-check" className="text-aw-green mt-0.5 shrink-0" />
                          Y ten la posibilidad de conceder cortesías completas sin restricciones de horario y cupo con un pase Free Total 
                        </li>
                      </ul>
                    </div>

                    {/* Acceso prioritario */}
                    <div className="bg-aw-bluedark rounded-lg border border-aw-green/30 p-4">
                      <p className="text-white font-semibold mb-2 flex items-center gap-2">
                        <FontAwesomeIcon icon="fa-solid fa-rocket" className="text-aw-green" />
                        Acceso prioritario
                      </p>
                      <ul className="space-y-1 text-gray-300 text-sm">
                        <li className="flex items-start gap-2">
                          <FontAwesomeIcon icon="fa-solid fa-check" className="text-aw-green mt-0.5 shrink-0" />
                          Primero en recibir nuevas funciones y actualizaciones <span className="text-gray-500 text-xs">(*)</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <p className="text-gray-500 text-xs mt-4">
                    <sup>(*)</sup> Sujeto a periodo de vigencia y permanencia.
                  </p>

                  <button
                    onClick={() => setShowBeneficios(false)}
                    className="mt-5 w-full py-2.5 rounded-lg bg-aw-green text-aw-blueblack font-semibold hover:scale-95 transition-all"
                  >
                    ¡Quiero un plan!
                  </button>
                </div>
              </Motion.div>
            </Motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
};

export default SuscripcionTab;
